import React, { useEffect, useMemo, useRef, useState, type JSX } from 'react';
import ReactECharts from 'echarts-for-react';
import { isNullOrUndefined } from '@fiji/common/src/utils/helpers';
import { GRID_ROWS } from '@fiji/common/src/constants';

type PlotBand = { from: number; to: number; color: string; thickness?: number; fillRatio?: number; fillColor?: string };
type ContainerSize = { width: number; height: number };

const GAUGE_TRACK_COLOR = '#eeeeee';
const AXIS_LABEL_COLOR = '#000000';
const VALUE_LABEL_COLOR = '#727E84';
const CRITICAL_COLOR = '#DF5353';
const WARNING_COLOR = '#DDDF0D';
const NORMAL_COLOR = '#55BF3B';
// Slightly smaller gap keeps more of each block's width visible.
const SEGMENT_GAP = 3;
const BAND_RADIUS = 4;
// Bar thickness scales with the measured container width so a small widget shows a chunky
// track while a larger widget shows a finer, thinner track.
const MIN_TRACK_WIDTH = 220;
const MAX_TRACK_WIDTH = 700;
const MIN_BAR_HEIGHT = 80;
const MAX_BAR_HEIGHT = 60;
// Segment count keeps each band's own pixel width fixed (~ MIN_TRACK_WIDTH / MIN_SEGMENTS)
// and grows the number of bands as the container gets wider, instead of stretching bands.
// Fewer minimum segments means each one gets a wider share of the track.
const MIN_SEGMENTS = 5;
const BAND_WIDTH = MIN_TRACK_WIDTH / MIN_SEGMENTS;

const isPreviewLikeMode = (mode?: string): boolean => mode === 'preview' || mode === 'configured';
// In preview/configured mode we want each block noticeably wider (fewer, larger segments) and
// the bar itself a bit shorter, so the track reads as a flatter, wider strip rather than the
// taller/denser look used in the normal dashboard/widget-grid rendering.
const PREVIEW_BAND_WIDTH_MULTIPLIER = 0.75;
const PREVIEW_BAR_HEIGHT_SHRINK = 0.55;

// Same dynamic height calculation used previously (Highcharts) to size the chart against the
// widget's grid dimensions.
const resolveDynamicHeight = (gaugeWidget: any, height?: number): number => {
    const baseHeight = gaugeWidget?.viewScale ? 100 : 70;
    return height ? Math.max(height * GRID_ROWS * 0.4, baseHeight) : baseHeight;
};

const scaleByWidth = (width: number, minValue: number, maxValue: number): number => {
    if (!width) return minValue;
    const ratio = Math.min(Math.max((width - MIN_TRACK_WIDTH) / (MAX_TRACK_WIDTH - MIN_TRACK_WIDTH), 0), 1);
    return minValue + ratio * (maxValue - minValue);
};

const resolveSegmentCount = (width: number, mode?: string): number => {
    if (!width) return MIN_SEGMENTS;
    const bandWidth = isPreviewLikeMode(mode) ? BAND_WIDTH * PREVIEW_BAND_WIDTH_MULTIPLIER : BAND_WIDTH;
    return Math.max(MIN_SEGMENTS, Math.round(width / bandWidth));
};

const resolveBarHeight = (width: number, mode?: string): number => {
    const barHeight = scaleByWidth(width, MIN_BAR_HEIGHT, MAX_BAR_HEIGHT);
    return Math.round(isPreviewLikeMode(mode) ? barHeight * PREVIEW_BAR_HEIGHT_SHRINK : barHeight);
};

// Rounds a rough step up to a "nice" 1/2/5 x 10^n number so axis ticks land on clean values
// (e.g. 1000, 2000, 5000) instead of arbitrary fractions.
const getNiceStep = (roughStep: number): number => {
    if (!Number.isFinite(roughStep) || roughStep <= 0) return 1;
    const exponent = Math.floor(Math.log10(roughStep));
    const magnitude = 10 ** exponent;
    const fraction = roughStep / magnitude;
    const niceFraction = fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 5 ? 5 : 10;
    return niceFraction * magnitude;
};

// One shared step drives both the axis tick spacing and the track's block boundaries, so
// every block lines up with exactly one axis label instead of blocks and labels drifting apart.
const resolveTickStep = (min: number, max: number, targetTicks: number): number => {
    const range = Math.max(max - min, 1);
    const roughStep = range / Math.max(targetTicks, 1);
    return getNiceStep(roughStep);
};

// Formats a tick value into a rounded K/M abbreviation (e.g. 1000 -> "1k", 1500000 -> "1.5M").
const formatAxisLabel = (value: number): string => {
    const absValue = Math.abs(value);
    if (absValue >= 1_000_000) {
        return `${Math.round((value / 1_000_000) * 10) / 10}M`;
    }
    if (absValue >= 1_000) {
        return `${Math.round((value / 1_000) * 10) / 10}k`;
    }
    return `${Math.round(value)}`;
};

// Resolves the fill color for the current value against threshold zones. Unlike the
// single-direction `findColor` helper (used elsewhere), this checks the low-side and
// high-side thresholds independently, so the fill is colored correctly regardless of
// whether one or both sides are configured.
const resolveValueColor = (threshold: any, value: number): string => {
    const { lowCritical, lowWarning, highWarning, highCritical } = threshold ?? {};
    if (!isNullOrUndefined(lowCritical) && value <= lowCritical) return CRITICAL_COLOR;
    if (!isNullOrUndefined(highCritical) && value >= highCritical) return CRITICAL_COLOR;
    if (!isNullOrUndefined(lowWarning) && value <= lowWarning) return WARNING_COLOR;
    if (!isNullOrUndefined(highWarning) && value >= highWarning) return WARNING_COLOR;
    return NORMAL_COLOR;
};

// Splits a band into fixed-width sub-segments (one per tick step) purely for the segmented-track
// look; the combined [from, to] range stays identical so the underlying business logic is
// unaffected. The final piece is truncated to the band's actual end when the range isn't an
// exact multiple of the step.
const subdivideBand = (band: PlotBand, step: number): PlotBand[] => {
    const span = band.to - band.from;
    if (span <= 0 || step <= 0) return [band];
    const pieces: PlotBand[] = [];
    let cursor = band.from;
    while (cursor < band.to - 1e-9) {
        const next = Math.min(cursor + step, band.to);
        pieces.push({ from: cursor, to: next, color: band.color });
        cursor = next;
    }
    return pieces.length ? pieces : [band];
};

// Renders the track as a fixed grid of blocks (same boundaries as the axis ticks/labels).
// Blocks fully below the value are fully colored, blocks fully above stay gray, and the one
// block the value actually falls inside gets a partial fill proportional to how far into that
// block the value sits — so the colored portion lines up precisely with the value instead of
// only lighting up whichever single coarse block the value happens to start in.
const buildTrackSegments = (
    gaugeWidget: any,
    min: number,
    max: number,
    isThreshold: boolean,
    value: number,
    tickStep: number
): PlotBand[] => {
    const clampedValue = Math.min(Math.max(value, min), max);
    const fillColor = isThreshold ? resolveValueColor(gaugeWidget?.threshold, value) : NORMAL_COLOR;

    const blocks = subdivideBand({ from: min, to: max, color: GAUGE_TRACK_COLOR }, tickStep);

    return blocks.map((block) => {
        const span = block.to - block.from;
        const fillRatio = span <= 0 ? 0 : Math.min(Math.max((clampedValue - block.from) / span, 0), 1);
        return { ...block, fillRatio, fillColor };
    });
};

const buildTrackOption = (
    segments: PlotBand[],
    min: number,
    max: number,
    showScale: boolean,
    barHeight: number,
    tickStep: number
): any => ({
    grid: { left: 2, right: 2, top: 2, bottom: showScale ? 18 : 2 },
    xAxis: {
        type: 'value',
        min,
        max,
        interval: tickStep,
        position: 'bottom',
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { show: false },
        axisLabel: { show: showScale, fontSize: 12, color: AXIS_LABEL_COLOR, formatter: formatAxisLabel },
    },
    yAxis: {
        type: 'category',
        data: ['track'],
        show: false,
    },
    series: [
        {
            type: 'custom',
            silent: true,
            renderItem: (params: any, api: any): any => {
                const segment = segments[params.dataIndex];
                if (!segment) return undefined;
                const start = api.coord([segment.from, 0]);
                const end = api.coord([segment.to, 0]);
                const coordSys = params.coordSys;
                const resolvedHeight = Math.min(barHeight, coordSys.height);
                const y = coordSys.y + (coordSys.height - resolvedHeight) / 2;
                const isFirst = params.dataIndex === 0;
                const isLast = params.dataIndex === segments.length - 1;
                const fullWidth = Math.max(end[0] - start[0] - (isLast ? 0 : SEGMENT_GAP), 0);
                const radius: [number, number, number, number] = [
                    isFirst ? BAND_RADIUS : 0,
                    isLast ? BAND_RADIUS : 0,
                    isLast ? BAND_RADIUS : 0,
                    isFirst ? BAND_RADIUS : 0,
                ];
                const fillWidth = fullWidth * (segment.fillRatio ?? 0);

                return {
                    type: 'group',
                    children: [
                        {
                            type: 'rect',
                            shape: { x: start[0], y, width: fullWidth, height: resolvedHeight, r: radius },
                            style: { fill: GAUGE_TRACK_COLOR },
                        },
                        ...(fillWidth > 0
                            ? [
                                  {
                                      type: 'rect',
                                      shape: { x: start[0], y, width: fillWidth, height: resolvedHeight, r: radius },
                                      style: { fill: segment.fillColor },
                                  },
                              ]
                            : []),
                    ],
                };
            },
            data: segments.map((_, index) => index),
        },
    ],
});

export const HorizontalGauge = ({
    gaugeWidget,
    pointConversion,
    height,
    mode,
}: {
    gaugeWidget: any;
    pointConversion: any;
    height?: number;
    mode?: string;
}): JSX.Element => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [containerSize, setContainerSize] = useState<ContainerSize>({ width: 0, height: 0 });

    useEffect(() => {
        const node = containerRef.current;
        if (!node) return;

        const updateSize = (): void => {
            const { width, height: measuredHeight } = node.getBoundingClientRect();
            setContainerSize((currentSize) => {
                if (currentSize.width === width && currentSize.height === measuredHeight) {
                    return currentSize;
                }
                return { width, height: measuredHeight };
            });
        };

        updateSize();

        const observer = new ResizeObserver(updateSize);
        observer.observe(node);
        window.addEventListener('resize', updateSize);

        return () => {
            observer.disconnect();
            window.removeEventListener('resize', updateSize);
        };
    }, []);

    const value = gaugeWidget?.data?.value ?? 0;
    const min = gaugeWidget?.min ?? 0;
    const max = gaugeWidget?.max ?? gaugeWidget?.data?.value ?? 0;
    const unit = gaugeWidget?.data?.unit ?? '';
    const isThreshold =
        Boolean(Object?.keys(gaugeWidget?.threshold ?? {}).length) && Boolean(gaugeWidget?.viewThreshold);
    const dynamicHeight = resolveDynamicHeight(gaugeWidget, height);
    const segmentCount = resolveSegmentCount(containerSize.width, mode);
    const barHeight = resolveBarHeight(containerSize.width, mode);
    const tickStep = resolveTickStep(min, max, segmentCount);

    const segments = useMemo(
        () => buildTrackSegments(gaugeWidget, min, max, isThreshold, value, tickStep),
        [gaugeWidget, min, max, isThreshold, value, tickStep]
    );

    const option = useMemo(
        () => buildTrackOption(segments, min, max, Boolean(gaugeWidget?.viewScale), barHeight, tickStep),
        [segments, min, max, gaugeWidget?.viewScale, barHeight, tickStep]
    );

    return (
        <div style={{ width: '100%', display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 12 }}>
            <p className="hc-cat-title" style={{ margin: 0, whiteSpace: 'nowrap', flexShrink: 0, marginBottom: 5 }}>
                {pointConversion(value)}
                <span style={{ fontSize: 18, fontWeight: 400, color: VALUE_LABEL_COLOR }}>{unit}</span>
            </p>
            <div ref={containerRef} style={{ flex: 1, minWidth: 0 }}>
                <ReactECharts
                    option={option}
                    style={{ height: dynamicHeight, width: '100%' }}
                    opts={{ renderer: 'svg' }}
                    notMerge={true}
                />
            </div>
        </div>
    );
};
